import {
	LIST_ITEM_KEY,
	ORDERED_LIST_KEY,
	UNORDERED_LIST_KEY,
	PARAGRAPH_KEY,
	SELECTION_KEY
} from '@techchange/pm-specs';
import { autoJoin } from 'prosemirror-commands';
import { wrapInList } from 'prosemirror-schema-list';
import { TextSelection } from 'prosemirror-state';

import getMarkedSelection from '../../utils/getMarkedSelection';
import { getSelectionListTypes } from '../../utils/getSelectionListTypes';
import markSelection from '../../utils/markSelection';

import { rebuildList } from './rebuildList';

/**
 * Toggles a list. Will wrap selected text nodes into a list of the given type,
 * unwrap selected text nodes from lists of the given type, or merge selected text nodes
 * and nodes of other list types into the a list of the given type.
 *
 * @param {Object} listType  The type of list to toggle.
 *
 * @returns {Object} Parametrized command that toggles the list type.
 *
 */
export const toggleList = (listType) => {

	return autoJoin((state, dispatch) => {

		const { selection, schema, tr } = state;

		// Node and mark type constants
		const paragraphNodeType = schema.nodes[PARAGRAPH_KEY];
		const listItemNodeType = schema.nodes[LIST_ITEM_KEY];
		const orderedListNodeType = schema.nodes[ORDERED_LIST_KEY];
		const unorderedListNodeType = schema.nodes[UNORDERED_LIST_KEY];
		const selectionMarkType = schema.marks[SELECTION_KEY];

		// The beginning and end positions of the selection
		const { from, to } = selection;

		// Add mark to selected inline nodes to remember original selection
		markSelection(state, tr);

		// Get the top-level nodes in the selection
		const selectedNodes = [];

		let containsListNodes = false, containsNonListNodes = false;

		tr.doc.nodesBetween(from, to, (node, pos) => {

			if (node.type === orderedListNodeType || node.type === unorderedListNodeType) {

				containsListNodes = true;

			} else {

				containsNonListNodes = true;

			}

			selectedNodes.push({
				node,
				pos,
			});

			// Don't recurse to child nodes
			return false;

		});

		if (!containsListNodes) {

			return wrapInList(listType)(state, dispatch);

		}

		// Otherwise, toggle the list.
		//
		// Toggling will result in one of the following changes:
		//	1. If the selection is all of the same list type and listType matches,
		//		the selection will be unwrapped.
		//	2. If the selection is all of the same list type and listType does not match,
		//		the list type of the selection will be changed to listType.
		//	3. If the selection contains lists of different types, the list type of the
		//		selection will be changed to listType.

		const containsMixedListTypes = getSelectionListTypes(from, to, state).length > 1;

		let newContent = [];

		selectedNodes.forEach((selectedNode) => {

			const { node, pos } = selectedNode;

			if (node.type === paragraphNodeType) {

				// Wrap in list
				const listItem = listItemNodeType.create({}, node);
				const list = listType.create({}, listItem);

				newContent.push(list);

			} else if (node.type === orderedListNodeType || node.type === unorderedListNodeType) {

				// Get a list of node(s) that reflect the requested changes
				const toggledList = rebuildList(listType, node, pos, state, {
					merge: (containsNonListNodes || containsMixedListTypes),
					unwrap: false
				});

				newContent = newContent.concat(toggledList);

			}

		});

		// Replace the selection with the new content
		const firstNode = selectedNodes[0];
		const lastNode = selectedNodes[selectedNodes.length - 1];
		const start = firstNode.pos;
		const end = lastNode.pos + lastNode.node.nodeSize;

		tr.replaceWith(start, end, newContent);

		// Find new selection positions based on marked nodes
		const [newFrom, newTo] = getMarkedSelection(state, tr);

		// Update the selection and remove the selection marks
		const isCursorSelection = from === to;

		const newSelection = TextSelection.create(
			tr.doc,
			newFrom || 0,
			isCursorSelection ? newFrom : (newTo || 0)
		);

		tr.setSelection(newSelection);
		tr.removeMark(0, tr.doc.content.size, selectionMarkType);

		// Remove placeholder for cursor selections
		if (isCursorSelection) {
			tr.delete(newFrom, newTo);
		}

		if (dispatch) {
			dispatch(tr);
		}

		return true;

	}, [ORDERED_LIST_KEY, UNORDERED_LIST_KEY]);

};
